<template>
  <div>
    <div class="d-flex justify-content-center w-100">
      <div
        v-if="listing.photoUrl"
        class="user-icon-wrapper"
      >
        <img
          class="photo"
          :src="listing.photoUrl"
        >
      </div>
      <div
        v-else
        class="user-icon-wrapper"
      >
        <div class="user-icon text-center">
          <i
            class="far"
            :class="catalogIcon(catalog.productCategory)"
          />
        </div>
      </div>
    </div>

    <div class="listing-name mt-2">
      {{ listing.name }}
    </div>

    <div class="text-center money-font mt-2">
      {{ formatCurrency(listing.price, listing.currency) }}
    </div>
    <hr>

    <div

      v-if="listing.orderFrom || listing.orderTo || listing.cancelTo || listing.countLimit >= 0"
      class="sub-title mt-2"
    >
      {{ $t('shop.availability') }}
    </div>
    <div class="listing-details mt-2">
      <div
        v-if="listing.orderFrom || listing.orderTo || listing.cancelTo || listing.countLimit >= 0"
        class="time-info"
      >
        <div v-if="listing.orderFrom">
          <span class="time-label">{{ $t('shop.orderFrom') }}:</span>
          <span class="time-value">{{ formatDate(listing.orderFrom) }}</span>
        </div>
        <div v-if="listing.orderTo">
          <span class="time-label">{{ $t('shop.orderTo') }}:</span>
          <span class="time-value">{{ formatDate(listing.orderTo) }}</span>
        </div>
        <div v-if="listing.cancelTo">
          <span class="time-label">{{ $t('shop.cancelTo') }}:</span>
          <span class="time-value">{{ formatDate(listing.cancelTo) }}</span>
        </div>
        <div
          v-if="listing.countLimit >= 0"
          class="count-info"
        >
          <span class="time-label">{{ $t('shop.spotsAvailable') }}:</span>
          <span class="time-value">
            {{ Math.max(0, listing.countLimit - listing.countBlocked) }} / {{ listing.countLimit }}
          </span>
        </div>
      </div>
    </div>

    <div class="sub-title mt-2">
      {{ $t('general.description') }}
    </div>
    <div
      class="mt-2 listing-details rich-description"
      v-html="listing.description"
    />
    <hr>

    <div
      v-if="isCommentRequired"
      class="mb-3"
    >
      <div class="sub-title mt-2">
        {{ $t('shop.comment') }}
      </div>
      <textarea
        v-model="comment"
        class="form-control mt-2"
        rows="3"
        :placeholder="$t('shop.enterComment')"
      />
    </div>

    <div
      v-if="showReserveRemove"
    >
      <div
        v-if="ordered"
        class="mb-3 small"
      >
        Czy na pewno chcesz odwołać to zamówienie?
      </div>
      <div
        v-else
        class="mb-3 small"
      >
        Czy na pewno chcesz usunąć tę pozycję z listy rezerwowej?
      </div>
      <div class="d-flex">
        <div class="flex-grow-1 mx-2">
          <button
            class="btn btn-secondary w-100"
            @click="showReserveRemove = false"
          >
            {{ $t('general.undo') }}
          </button>
        </div>
        <div class="flex-grow-1 mx-2">
          <button
            class="btn btn-danger w-100"
            :disabled="reserveQueuePending"
            @click="confirmRemove"
          >
            {{ ordered ? $t('shop.cancel') : $t('general.remove') }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="canOrderMany && isCommentValid"
      @click.stop
    >
      <CounterInput
        :value="count"
        :min-value="minAllowedCount"
        :max-value="maxAllowedCount"
        @input="toggleCount"
      />
    </div>

    <AvailabilityBottomButton
      v-else-if="!canOrderMany && !showReserveRemove"
      :status="status"
      :availability="listing"
      :cancel-to="listing.cancelTo"
      :order-from="listing.orderFrom"
      :order-to="listing.orderTo"
      :count-exceeded="countExceeded"
      :disabled="!isCommentValid"
      @toggle="handleToggle"
    />
  </div>
</template>

<script>
import axios from '@/api/axios';
import Bowser from 'bowser';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import CounterInput from '@/components/utils/CounterInput';
import AvailabilityBottomButton from './AvailabilityBottomButton';

export default {
  props: {
    catalog: Object,
    listing: Object,
    payerId: String,
    ordered: Object,
    reserved: Object,
    canOrderMany: Boolean,
  },
  data: () => ({
    count: 1,
    initialCount: 0,
    comment: '',
    showReserveRemove: false,
    reserveQueuePending: false,
  }),
  components: {
    CounterInput,
    AvailabilityBottomButton,
  },
  computed: {
    ...mapGetters([
      'catalogIcon',
      'formatCurrency',
      'catalogBasket',
    ]),
    status() {
      if (this.ordered) {
        if (this.basketEntry) {
          return 'paidCancelled';
        }
        return 'paid';
      }
      if (this.reserved) {
        return 'reserveQueue';
      }
      if (this.basketEntry) {
        return 'inBasket';
      }
      return 'available';
    },
    countExceeded() {
      return this.listing.countLimit >= 0
        && this.listing.countBlocked >= this.listing.countLimit;
    },
    basketEntry() {
      return this.catalogBasket
        .find((x) => x.payerId === this.payerId && x.offerId === this.listing.id);
    },
    isCommentRequired() {
      return this.listing.form === 'comment';
    },
    isCommentValid() {
      return !this.isCommentRequired || (this.comment && this.comment.trim().length > 0);
    },
    isCancelDatePassed() {
      if (!this.listing.cancelTo) return false;
      return moment().isAfter(moment(this.listing.cancelTo));
    },
    isOrderToDatePassed() {
      if (!this.listing.orderTo) return false;
      return moment().isAfter(moment(this.listing.orderTo));
    },
    isOrderFromDatePassed() {
      if (!this.listing.orderFrom) return true; // No orderFrom means always allowed
      return moment().isAfter(moment(this.listing.orderFrom));
    },
    minAllowedCount() {
      // If cancel date has passed and user has already ordered, they cannot reduce below that
      if (this.isCancelDatePassed && this.ordered) {
        return this.ordered.count || 0;
      }
      return 0;
    },
    maxAllowedCount() {
      // If orderFrom date hasn't passed, cannot increase above current ordered count
      if (!this.isOrderFromDatePassed) {
        return this.ordered?.count || 0;
      }
      // If orderTo date has passed and user has already ordered, they cannot increase above that
      if (this.isOrderToDatePassed && this.ordered) {
        return this.ordered.count || 0;
      }
      return 100;
    },
  },
  watch: {
    comment(newVal) {
      if (this.isCommentRequired && (!newVal || newVal.trim().length === 0)) {
        // Comment was cleared, reset to initial count
        if (this.basketEntry) {
          this.removeFromBasket();
        }
        this.count = this.initialCount;
      }
    },
  },
  methods: {
    ...mapActions([
      'createOrder',
    ]),
    formatDate(date) {
      return moment(date).format('DD MMMM YYYY HH:mm');
    },
    confirmRemove() {
      this.reserveQueuePending = true;
      if (this.ordered) {
        this.createOrder({
          params: {
            query: {
              payeeId: this.catalog.payeeId,
            },
          },
          data: {
            isApp: false,
            origin: JSON.stringify(Bowser.parse(window.navigator.userAgent)),
            version: process.env.VUE_APP_VERSION || '?',
            catalogId: this.catalog.id,
            addedItems: [],
            cancelledItems: [
              {
                availabilityId: this.listing.id,
                payerId: this.payerId,
                day: '2000-01-01',
                count: 1,
              },
            ],
          },
        })
          .then(() => {
            this.$emit('refresh');
            this.$emit('close');
          })
          .finally(() => {
            this.reserveQueuePending = false;
          });
      } else {
        axios.put('user/shop/reserve-remove', {
          payerId: this.payerId,
          catalogId: this.catalog.id,
          listingId: this.listing.id,
        })
          .then(() => {
            this.$emit('refresh');
            this.$emit('close');
          })
          .finally(() => {
            this.reserveQueuePending = false;
          });
      }
    },
    toggleCount(v) {
      const currentOrderedCount = this.ordered?.count || 0;

      // Prevent reducing quantity if cancel date has passed
      if (this.isCancelDatePassed && this.ordered && v < currentOrderedCount) {
        this.count = currentOrderedCount;
        return;
      }

      // Prevent increasing quantity if orderFrom date hasn't passed
      if (!this.isOrderFromDatePassed && v > currentOrderedCount) {
        this.count = currentOrderedCount;
        return;
      }

      // Prevent increasing quantity if orderTo date has passed
      if (this.isOrderToDatePassed && this.ordered && v > currentOrderedCount) {
        this.count = currentOrderedCount;
        return;
      }

      this.count = v;

      if (this.count === currentOrderedCount) {
        this.removeFromBasket();
        return;
      }

      if (this.basketEntry) {
        this.removeFromBasket();
      }

      const countInBasket = (this.count - currentOrderedCount);
      const isReturned = this.count < currentOrderedCount;

      this.addToBasket(countInBasket, isReturned);
    },
    handleToggle() {
      if (this.reserved || this.ordered) {
        this.showReserveRemove = true;
        return;
      }

      if (this.basketEntry) {
        this.removeFromBasket();
        this.$emit('close');
      } else {
        this.addToBasket(1, !!this.ordered);
        this.$emit('close');
      }
    },
    removeFromBasket() {
      this.$store.commit('removeFromBasket', {
        payerId: this.payerId,
        offerId: this.listing.id,
        isReturned: !!this.ordered,
      });
    },
    addToBasket(count, isReturned) {
      if (!this.isCommentValid) {
        return;
      }

      this.$store.commit('addBasketCatalog', {
        payerId: this.payerId,
        price: this.listing.price,
        value: this.listing.price,
        currency: this.listing.currency,
        payeeId: this.catalog.payeeId,
        catalogId: this.catalog.id,
        offerName: this.listing.name,
        offerId: this.listing.id,
        catalog: this.catalog,
        listing: this.listing,
        isReturned,
        count: Math.abs(count),
        status: 'inBasket',
        day: '2000-01-01',
        comment: this.comment || undefined,
      });
    },
  },
  created() {
    const basketCount = this.basketEntry?.isReturned
      ? -this.basketEntry?.count
      : this.basketEntry?.count || 0;

    this.count = (this.ordered?.count || 0) + basketCount;
    this.initialCount = this.count;
    this.comment = this.basketEntry?.comment || this.ordered?.comment || '';
  },
};
</script>

<style lang="scss" scoped>
.listing-name {
  font-weight: 500;
  text-align: center;
}

.photo {
  max-height: 200px;
  max-width: 200px;
}

.sub-title {
  font-size: 16px;
  font-weight: 500;
}

.user-icon {
  color: #aaa;
  font-size: 20px;
  background-color: #D9E9FF;
  width: 40px;
  height: 40px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $blue;
}

.user-icon-wrapper {
  width: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.listing-details {
  font-size: 0.9rem;
  color: #666;
}

.rich-description {
  ::v-deep p:last-child {
    margin-bottom: 0;
  }

  ::v-deep ul,
  ::v-deep ol {
    padding-left: 1.2rem;
    margin-bottom: 0.25rem;
  }
}

.time-info, .count-info {
  text-align: left;
}

.time-value {
  font-weight: 500;
  color: #333;
}

.time-label {
  color: #666;
  margin-right: 5px;
}
</style>
